// Cycle-16 image-attachment card. Mirrors `PingImageAttachmentCard`
// (iOS-A) / `ImageAttachmentCard` (AND-A). Renders one of three states
// from the Figma `Image attachment` (`3412:5288`) /
// `Image attachment / failed` (`3412:5294`) frames. The blurhash is
// rendered as an opaque placeholder swatch — the host is expected to
// pre-decode it via a worker and pass an `imageUri` once the upload
// resolves; this primitive doesn't decode anything.
//
// Figma node geometry:
//   Outer wrap    (I3412:5289;1828:59031): 300×398, radius 16, padding 2,
//                                          gap 4, fill `surface.secondary`
//                                          (or indigo for self)
//   Image frame   (I3412:5289;1828:59033): 296×394 with radius 14 (the
//                                          inner 16 - 2 px padding)
//   Failed copy   (3412:5298 + 3412:5299) : "Resend image" 16 pt Bold
//                                          on a black-55 % overlay,
//                                          "Error sending image." 10 pt
//                                          rose caption below the wrap.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Image, Pressable, StyleSheet, Text, type TextStyle, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { Info, Play, RotateCw, Video } from 'lucide-react-native';
import { tokens } from '../../design-system/tokens';
import { clampAspectRatio } from '../messageFormatters';
import type { ImageAttachmentState } from './ImageAttachmentState';
import { ReadReceiptOverlay, type ReadByReader } from '../ReadReceiptOverlay';
import { useTileMaxWidth } from '../tileConstraint';

export interface ImageAttachmentCardProps {
  state: ImageAttachmentState;
  aspectRatio: number;
  blurhash?: string | undefined;
  imageUri?: string | undefined;
  progress?: number | undefined;
  /** Optional — when true the card renders with the indigo wrap. */
  isSelf?: boolean | undefined;
  /**
   * Optional — when true this image is a sticker: it renders with NO
   * background, NO padding and NO rounded-corner clip (just the image), for
   * both self and other. Overrides `isSelf`'s indigo wrap + the placeholder
   * swatch + the grouped-run corner blending.
   */
  isSticker?: boolean | undefined;
  /**
   * Optional — when true the card is a video preview: a centered 42×42
   * play disc overlays the frame and a bottom-left duration pill renders.
   */
  isVideo?: boolean | undefined;
  /** Optional duration label for the video pill, e.g. '00:13'. */
  durationLabel?: string | undefined;
  /**
   * Optional caption rendered below the image frame (sent alongside the
   * image from the composer's attachment-preview overlay). text.primary
   * 14/19, left-aligned, inside the card's horizontal inset.
   */
  caption?: string | undefined;
  /** Pre-formatted send time (e.g. "12:33") — overlaid bottom-right. */
  timeLabel?: string | undefined;
  /** Own + delivered → muted double-check beside the overlay time (Figma 7038:2229). */
  delivered?: boolean | undefined;
  /** Own + read → green double-check (takes precedence over `delivered`). */
  read?: boolean | undefined;
  /**
   * Grouped-run position (same-sender consecutive tiles). When set, the
   * corners on the tail side (right for self, left for other) flatten
   * where the tile adjoins a neighbour so a media run reads as one
   * connected stack — parity with text bubbles. Defaults to a standalone
   * (fully-rounded) tile.
   */
  isFirstOfGroup?: boolean | undefined;
  isLastOfGroup?: boolean | undefined;
  onTapOpen: () => void;
  onRetry?: (() => void) | undefined;
  /** Group chats only — resolved reader list. When non-empty the tick becomes
   *  a pressable that opens a "Read by" popover anchored to the tick pill. */
  readers?: ReadByReader[] | undefined;
  testID?: string | undefined;
}

// iMessage-style blended corners for a vertical media run. Tail side =
// right for self, left for other. Returns the OUTER wrap radii; the inner
// frame uses `r - 2` (the wrap's 2px padding). Mirrors MessageBubble.
function groupedRadii(
  isSelf: boolean | undefined,
  isFirstOfGroup: boolean | undefined,
  isLastOfGroup: boolean | undefined,
): { wrap: object; inner: object } {
  const FULL = 16;
  const TIGHT = 6;
  // A standalone tile (no group flags) stays fully rounded.
  if (isFirstOfGroup === undefined && isLastOfGroup === undefined) {
    return { wrap: {}, inner: {} };
  }
  const tailTop = isFirstOfGroup !== false ? FULL : TIGHT;
  const tailBottom = isLastOfGroup !== false ? FULL : TIGHT;
  const i = (r: number): number => Math.max(0, r - 2);
  if (isSelf) {
    return {
      wrap: {
        borderTopLeftRadius: FULL,
        borderBottomLeftRadius: FULL,
        borderTopRightRadius: tailTop,
        borderBottomRightRadius: tailBottom,
      },
      inner: {
        borderTopLeftRadius: i(FULL),
        borderBottomLeftRadius: i(FULL),
        borderTopRightRadius: i(tailTop),
        borderBottomRightRadius: i(tailBottom),
      },
    };
  }
  return {
    wrap: {
      borderTopRightRadius: FULL,
      borderBottomRightRadius: FULL,
      borderTopLeftRadius: tailTop,
      borderBottomLeftRadius: tailBottom,
    },
    inner: {
      borderTopRightRadius: i(FULL),
      borderBottomRightRadius: i(FULL),
      borderTopLeftRadius: i(tailTop),
      borderBottomLeftRadius: i(tailBottom),
    },
  };
}

// Deterministic placeholder swatch derived from the blurhash without
// pulling a decode dependency: hash the blurhash to a single hex token
// so two identical blurhashes render identical placeholders. The host
// supplies the real decoded preview via `imageUri` once it's available.
function placeholderColor(blurhash: string | undefined, fallback: string): string {
  if (!blurhash || blurhash.length === 0) {
    return fallback;
  }
  let hash = 0;
  for (let i = 0; i < blurhash.length; i += 1) {
    hash = (hash * 31 + blurhash.charCodeAt(i)) | 0;
  }
  const r = (hash & 0xff).toString(16).padStart(2, '0');
  const g = ((hash >> 8) & 0xff).toString(16).padStart(2, '0');
  const b = ((hash >> 16) & 0xff).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

export const ImageAttachmentCard = React.memo<ImageAttachmentCardProps>(
  ({
    state,
    aspectRatio,
    blurhash,
    imageUri,
    progress,
    isSelf,
    isSticker,
    isVideo,
    durationLabel,
    caption,
    timeLabel,
    delivered,
    read,
    isFirstOfGroup,
    isLastOfGroup,
    onTapOpen,
    onRetry,
    readers,
    testID,
  }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];
    // Inside a thread side panel, cap the tile to the provided width.
    const tileMax = useTileMaxWidth();
    const corners = useMemo(
      // Stickers are unclipped ("just the image") — never blend grouped-run
      // corners into them.
      () => (isSticker === true ? { wrap: {}, inner: {} } : groupedRadii(isSelf, isFirstOfGroup, isLastOfGroup)),
      [isSticker, isSelf, isFirstOfGroup, isLastOfGroup],
    );

    const ratio = useMemo(() => clampAspectRatio(aspectRatio), [aspectRatio]);
    const placeholder = useMemo(
      // A sticker has no placeholder swatch — the frame stays transparent so
      // only the (transparent-PNG) sticker art shows.
      () => (isSticker === true ? 'transparent' : placeholderColor(blurhash, colors.surface.tertiary)),
      [isSticker, blurhash, colors.surface.tertiary],
    );

    const isFailed = state === 'failed';
    const isUploading = state === 'uploading';
    // Resolving the decrypted blob URL (no imageUri yet, but not actively
    // uploading) → show a shimmer skeleton, never the "Uploading" overlay.
    const isLoading = state === 'loading' && imageUri === undefined;
    // Broken/expired media URL — flip to a small inline error state instead
    // of rendering a silent blank frame. Reset when a fresh (e.g. re-signed)
    // URL arrives so a retry can recover.
    const [imgError, setImgError] = useState(false);
    useEffect(() => {
      setImgError(false);
    }, [imageUri]);

    // Figma outer wrap fill: indigo for self, dark surface secondary
    // otherwise. The wrap does NOT change for the failed state — the
    // rose "Error sending image." caption is rendered below the wrap.
    // Stickers NEVER get a wrap fill (self or other) — just the image.
    const wrapBackground = isSticker === true
      ? 'transparent'
      : isSelf
        ? colors.accent.brand
        : colors.surface.elevated;
    const foreground = colors.text.onAccent;

    const noun = isVideo ? 'Video' : 'Image';
    const accessibilityLabel = useMemo(() => {
      if (isFailed) {
        return `${noun} failed to upload`;
      }
      if (isUploading) {
        const pct = progress !== undefined ? Math.round(progress * 100) : null;
        return pct !== null ? `${noun} uploading, ${pct}%` : `${noun} uploading`;
      }
      return `${noun} attachment`;
    }, [isFailed, isUploading, progress, noun]);

    // Failed-state caption string — branch on direction + media kind per
    // Figma 3412:5299 / 3412:5311.
    let errorCaption: string;
    if (!isSelf) {
      // Inbound failures are always a download failure regardless of kind.
      errorCaption = 'Error downloading image.';
    } else {
      errorCaption = isVideo ? 'Error sending video message.' : 'Error sending image.';
    }

    const overlayTextStyle: TextStyle = {
      color: foreground,
      fontWeight: '700',
    };

    return (
      <View style={styles.container}>
        {/* Relative wrapper so the tick pill (sibling to the card Pressable)
            can be positioned absolutely without nesting inside the Pressable
            — nested `<button>` inside `<button>` is invalid HTML and breaks
            pointer-event capture on web. */}
        <View>
          <Pressable
            onPress={isFailed ? onRetry : onTapOpen}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            accessibilityHint={isFailed ? 'Retry upload' : 'Open full image'}
            style={[
              styles.wrap,
              isSticker === true ? styles.stickerWrap : null,
              tileMax !== null ? { maxWidth: tileMax } : null,
              { backgroundColor: wrapBackground },
              corners.wrap,
            ]}
            testID={testID}
          >
            <View
              style={[
                styles.imageFrame,
                isSticker === true ? styles.stickerFrame : null,
                {
                  aspectRatio: ratio,
                  backgroundColor: placeholder,
                },
                corners.inner,
              ]}
            >
              {imageUri ? (
                <Image
                  source={{ uri: imageUri }}
                  style={styles.image}
                  accessibilityIgnoresInvertColors
                  onError={() => setImgError(true)}
                />
              ) : null}
              {isVideo && !isUploading && !isFailed ? (
                <>
                  <View style={styles.overlay} pointerEvents="none">
                    <View style={styles.playDisc}>
                      <Play size={20} color="#4B515C" fill="#4B515C" />
                    </View>
                  </View>
                  <View style={styles.durationPill} pointerEvents="none">
                    <Video size={12} color="#B5BAC3" />
                    <Text style={styles.durationText}>{durationLabel ?? '00:13'}</Text>
                  </View>
                </>
              ) : null}
              {isUploading ? (
                <View style={[styles.overlay, { backgroundColor: 'rgba(0, 0, 0, 0.45)' }]}>
                  <Text style={[styles.uploadingText, overlayTextStyle]}>
                    {progress !== undefined ? `${Math.round(progress * 100)}%` : 'Uploading'}
                  </Text>
                </View>
              ) : null}
              {isLoading ? <Shimmer baseColor={colors.surface.tertiary} /> : null}
              {imgError && !isFailed ? (
                <View style={[styles.overlay, { backgroundColor: colors.surface.tertiary }]}>
                  <Text style={[styles.loadErrorText, { color: colors.text.secondary }]}>
                    {`Couldn't load ${isVideo ? 'video' : 'image'}`}
                  </Text>
                </View>
              ) : null}
              {isFailed ? (
                // Figma 7038:2235 — a centered dark PILL with a retry glyph
                // + label. Outbound (self) → "Resend video"/"Resend image";
                // inbound download failure → "Retry".
                <View style={[styles.overlay, { backgroundColor: 'rgba(0, 0, 0, 0.45)' }]}>
                  <View style={styles.resendPill}>
                    <RotateCw size={18} strokeWidth={2.5} color="#F5F6F8" />
                    <Text style={[styles.resendText, overlayTextStyle]}>
                      {!isSelf ? 'Retry' : isVideo ? 'Resend video' : 'Resend image'}
                    </Text>
                  </View>
                </View>
              ) : null}
            </View>
          </Pressable>
          {/* Time+tick: sibling to the card Pressable — avoids nested buttons. */}
          {timeLabel !== undefined && !isUploading && !isFailed && !isLoading ? (
            <ReadReceiptOverlay
              timeLabel={timeLabel}
              {...(delivered !== undefined ? { delivered } : {})}
              {...(read !== undefined ? { read } : {})}
              isSelf={isSelf === true}
              {...(readers !== undefined ? { readers } : {})}
              style={styles.timeOverlay}
            />
          ) : null}
        </View>
        {caption !== undefined && caption.length > 0 ? (
          <Text
            style={[
              styles.caption,
              { color: isSelf ? colors.text.onAccent : colors.text.primary },
            ]}
          >
            {caption}
          </Text>
        ) : null}
        {isFailed ? (
          <View style={styles.metaRow}>
            <Text style={[styles.errorText, { color: '#FF5353' }]}>{errorCaption}</Text>
            <Info size={10} color="#FF5353" />
          </View>
        ) : null}
      </View>
    );
  },
);

ImageAttachmentCard.displayName = 'ImageAttachmentCard';

// Skeleton shimmer shown while the decrypted blob URL resolves (e.g. just
// after a reload). A gentle opacity pulse over the placeholder swatch —
// cross-platform (RN Animated, no extra dep), and crucially NOT the
// "Uploading %" overlay (the message is already sent/received).
function Shimmer({ baseColor }: { baseColor: string }): JSX.Element {
  const op = useRef(new Animated.Value(0.35)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(op, {
          toValue: 0.7,
          duration: 750,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(op, {
          toValue: 0.35,
          duration: 750,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [op]);
  return (
    <Animated.View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, { backgroundColor: baseColor, opacity: op }]}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    gap: tokens.spacing.xs,
  },
  // Outer wrap — Figma I3412:5289;1828:59031 (300 wide, radius 16,
  // padding 2).
  wrap: {
    width: 300,
    maxWidth: '100%',
    padding: 2,
    borderRadius: 16,
  },
  // Inner image frame — Figma I3412:5289;1828:59033 (radius 14 = outer
  // 16 - 2 px padding). `overflow: hidden` clips the rendered image to
  // the rounded corners.
  imageFrame: {
    borderRadius: 14,
    overflow: 'hidden',
  },
  // Sticker overrides: no bubble chrome (no fill via `wrapBackground`
  // 'transparent'), no padding, no rounding, and a sticker-sized width so
  // the art reads as a sticker rather than a full-width media card.
  stickerWrap: {
    width: 160,
    padding: 0,
    borderRadius: 0,
  },
  stickerFrame: {
    borderRadius: 0,
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Resend overlay PILL — Figma 7038:2235: a dark translucent pill with
  // a retry glyph + label, centered over the dimmed frame.
  resendPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
  },
  // Resend overlay text — Figma 3412:5298 ("Resend image" 16 pt Bold).
  resendText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 16,
    lineHeight: 22,
  },
  // Uploading text — sized like the resend label so the two overlay
  // affordances feel identical.
  uploadingText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 16,
    lineHeight: 22,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
    paddingHorizontal: tokens.spacing.xs,
  },
  // Centered video play disc — Figma 3412:5288 (42×42 pill, #E8EAED fill,
  // play triangle tinted #4B515C).
  playDisc: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E8EAED',
  },
  // Bottom-left duration pill — small play glyph (12) + '00:13' label
  // (10 pt, #B5BAC3) on a translucent dark scrim.
  durationPill: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
  },
  durationText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 10,
    lineHeight: 14,
    color: '#B5BAC3',
  },
  // Bottom-right send-time overlay — Figma 7038:2229 (white text + tick
  // on a translucent dark scrim). +2 offset vs. the old imageFrame-relative
  // position to account for the wrap's 2px padding now that the pill sits
  // outside imageFrame as a sibling to the card Pressable.
  timeOverlay: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 999,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
  },
  timeOverlayText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 10,
    lineHeight: 14,
    color: '#FFFFFF',
  },
  // Image caption — composer-staged caption sent with the image.
  // Sits below the frame at the card's horizontal inset (14/19).
  caption: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 14,
    lineHeight: 19,
    paddingHorizontal: tokens.spacing.xs,
  },
  // Error caption — Figma 3412:5299 / 3412:5311 (10 pt Lato 400, rose).
  errorText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 10,
    lineHeight: 14,
  },
  // Inline "couldn't load" copy shown over an opaque swatch when the
  // decrypted/re-signed media URL fails to render (broken/expired).
  loadErrorText: {
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: 12,
    lineHeight: 16,
    paddingHorizontal: 12,
    textAlign: 'center',
  },
});
